import type {
  ICanBeChartedProperty,
  IChartBuilderData,
} from '@principle-theorem/reporting/interfaces';
import {
  INamedDocument,
  isISODateType,
  TypeGuard,
  type ISODateType,
  type ISoftDelete,
} from '@principle-theorem/shared';
import { type ComparableValue } from 'crossfilter2';
import { isString } from 'lodash';
import { type RestrictAccessEntity } from '../auth/restrict-access';
import { TimingDirection, TimingUnit } from '../automation/automation-timing';
import { IStaffer } from '../staffer/staffer';
import { DataPointValue, type MeasureReducer } from './measures';
import { type IQueryScopeRequests } from './query-scopes';

// TODO: Stronger Type Enforcement of these
export type ReportingReference = string;
export const isReportingReference = isString;

export interface ICustomReportColumn {
  uid: string;
  id: ReportingReference;
  label?: string;
}

export const isCustomReportColumn = TypeGuard.interface<ICustomReportColumn>({
  uid: isString,
  id: isReportingReference,
  label: TypeGuard.undefinedOr(isString),
});

export interface ICustomReportDefinedColumn extends ICustomReportColumn {
  measure: ICanBeChartedProperty;
}

export type DateRangeFilter = [ISODateType, ISODateType];

export function isDateRangeFilter(data: unknown): data is DateRangeFilter {
  return (
    Array.isArray(data) &&
    data.length === 2 &&
    data.every((date) => isISODateType(date))
  );
}

export interface ICustomReportFilterValue {
  label: string;
  value: DataPointValue | ISODateType | DateRangeFilter;
}

export enum ReportFilterComparison {
  // IsEmpty = 'isEmpty',
  // IsNotEmpty = 'isNotEmpty',

  // Text Comparisons
  TextIsEqualTo = 'textIsEqualTo',
  TextContains = 'textContains',
  TextDoesNotContain = 'textDoesNotContain',
  TextStartsWith = 'textStartsWith',
  TextEndsWith = 'textEndsWith',
  TextIsExactly = 'textIsExactly',

  // Date Comparisons
  DateIs = 'dateIs',
  DateIsBefore = 'dateIsBefore',
  DateIsAfter = 'dateIsAfter',
  DateIsBetween = 'dateIsBetween',
  DateIsAtLeast = 'dateIsAtLeast',
  DateIsAtMost = 'dateIsAtMost',

  // Numeric Comparisons
  GreaterThan = 'greaterThan',
  GreaterThanOrEqualTo = 'greaterThanOrEqualTo',
  LessThan = 'lessThan',
  LessThanOrEqualTo = 'lessThanOrEqualTo',
  IsEqualTo = 'isEqualTo',
  IsNotEqualTo = 'isNotEqualTo',
}

export const TEXT_FILTER_COMPARISONS = [
  ReportFilterComparison.TextIsEqualTo,
  ReportFilterComparison.TextContains,
  ReportFilterComparison.TextDoesNotContain,
  ReportFilterComparison.TextStartsWith,
  ReportFilterComparison.TextEndsWith,
  ReportFilterComparison.TextIsExactly,
];

export const DATE_FILTER_COMPARISONS = [
  ReportFilterComparison.DateIs,
  ReportFilterComparison.DateIsBefore,
  ReportFilterComparison.DateIsAfter,
  ReportFilterComparison.DateIsBetween,
  ReportFilterComparison.DateIsAtLeast,
  ReportFilterComparison.DateIsAtMost,
];

export const STATIC_DATE_COMPARISONS = [
  ReportFilterComparison.DateIs,
  ReportFilterComparison.DateIsBefore,
  ReportFilterComparison.DateIsAfter,
];

export const DATE_RANGE_COMPARISONS = [ReportFilterComparison.DateIsBetween];

export const DYNAMIC_DATE_COMPARISONS = [
  ReportFilterComparison.DateIsAtLeast,
  ReportFilterComparison.DateIsAtMost,
];

export const SELECT_FILTER_COMPARISONS = [
  ReportFilterComparison.IsEqualTo,
  ReportFilterComparison.IsNotEqualTo,
];

export const NUMBER_FILTER_COMPARISONS = [
  ReportFilterComparison.IsEqualTo,
  ReportFilterComparison.IsNotEqualTo,
  ReportFilterComparison.GreaterThan,
  ReportFilterComparison.GreaterThanOrEqualTo,
  ReportFilterComparison.LessThan,
  ReportFilterComparison.LessThanOrEqualTo,
];

export const REPORT_FILTER_COMPARISON_LABELS: {
  [key in ReportFilterComparison]: string;
} = {
  // TODO: Implement these comparisons
  // [ReportFilterComparison.IsEmpty]: 'Empty',
  // [ReportFilterComparison.IsNotEmpty]: 'Not empty',

  // Text Comparisons
  [ReportFilterComparison.TextIsEqualTo]: 'Is equal to',
  [ReportFilterComparison.TextContains]: 'Contains',
  [ReportFilterComparison.TextDoesNotContain]: 'Does not contain',
  [ReportFilterComparison.TextStartsWith]: 'Starts with',
  [ReportFilterComparison.TextEndsWith]: 'Ends with',
  [ReportFilterComparison.TextIsExactly]: 'Is exactly',

  // Date Comparisons
  [ReportFilterComparison.DateIs]: 'Is',
  [ReportFilterComparison.DateIsBefore]: 'Is before',
  [ReportFilterComparison.DateIsAfter]: 'Is after',
  [ReportFilterComparison.DateIsBetween]: 'Is between',

  // Relative Date Comparisons
  [ReportFilterComparison.DateIsAtLeast]: 'Is at least (relative)',
  [ReportFilterComparison.DateIsAtMost]: 'Is at most (relative)',

  // Numeric Comparisons
  [ReportFilterComparison.GreaterThan]: 'Greater than',
  [ReportFilterComparison.GreaterThanOrEqualTo]: 'Greater than or equal to',
  [ReportFilterComparison.LessThan]: 'Less than',
  [ReportFilterComparison.LessThanOrEqualTo]: 'Less than or equal to',
  [ReportFilterComparison.IsEqualTo]: 'Equal to',
  [ReportFilterComparison.IsNotEqualTo]: 'Not equal to',
};

export interface IRelativeDateOffset {
  direction: TimingDirection;
  amount: number;
  unit: TimingUnit;
}

export enum DateFilterType {
  ReportDate = 'reportDate',
  SpecificDate = 'specificDate',
}

export interface IFilterPlaceholder {
  isPlaceholder?: boolean;
  placeholderMessage?: string;
}

export interface ICustomReportFilter extends IFilterPlaceholder {
  uid: string;
  id: ReportingReference;
  values: ICustomReportFilterValue[];
  comparison?: ReportFilterComparison;
  dateType?: DateFilterType;
  relativeOffset?: IRelativeDateOffset;
}

export interface IBaseChartBuilderConfig {
  height: number;
  width: number;
  disableControls?: boolean;
  numberOfTicks?: number;
  rotateXAxisLabels?: boolean;
}

export interface IColumnChartBuilderConfig extends IBaseChartBuilderConfig {
  rotateXAxisLabels?: boolean;
}

export interface ICustomReportChart<
  T extends ICustomChartSettings = ICustomChartSettings,
  Config extends IBaseChartBuilderConfig = IBaseChartBuilderConfig,
> extends IFilterPlaceholder {
  uid: string;
  type: CustomChartType;
  label: string;
  groupBy: ReportingReference;
  settings: T[];
  builderData?: Partial<
    Omit<IChartBuilderData, 'measures' | 'groupByDimension'>
  >;
  builderConfig?: Partial<Config>;
}

export interface ICustomChartSettings {
  uid: string;
  label: string;
  dataPoint: ReportingReference;
  reducer: MeasureReducer;
  filters?: ComparableValue[];
}

export interface ICustomReportChartSection {
  uid: string;
  name: string;
  charts: ICustomReportChart<ICustomChartSettings>[];
}

export interface ICustomReportData {
  staticFilters?: ICustomReportFilter[];
  chartSections?: ICustomReportChartSection[];
  columns?: ICustomReportColumn[];
}

export enum CustomReportStatus {
  Draft = 'draft',
  Published = 'published',
}

export const CUSTOM_REPORT_STATUS_COLOUR_MAP: Record<
  CustomReportStatus,
  string
> = {
  [CustomReportStatus.Draft]: 'default',
  [CustomReportStatus.Published]: 'success',
};

export interface ICustomReport
  extends Required<ICustomReportData>,
    ISoftDelete {
  name: string;
  description?: string;
  restrictAccessTo: RestrictAccessEntity[];
  dataSource: ReportBuilderDataSourceId;
  queryScopes: IQueryScopeRequests;
  status: CustomReportStatus;
  creator?: INamedDocument<IStaffer>;
}

export enum CustomChartType {
  NumberSummary = 'numberSummary',
  Table = 'table',
  Line = 'line',
  Pie = 'pie',
  Bar = 'bar',
  Row = 'row',
  Column = 'column',
  SelectMenu = 'selectMenu',
  StackedArea = 'stackedArea',
  Histogram = 'histogram',
  StackedBar = 'stackedBar',
  SanKey = 'sanKey',
}

export const ENABLED_CHART_TYPES = [
  CustomChartType.NumberSummary,
  CustomChartType.Table,
  CustomChartType.Pie,
  CustomChartType.Row,
  CustomChartType.Column,
  CustomChartType.SelectMenu,
];

export enum ReportBuilderDataSourceId {
  AccountCredits = 'accountCredit',
  Appointments = 'appointments',
  Invoices = 'invoices',
  LabJobs = 'labJobs',
  Patients = 'patients',
  PatientInteractions = 'patientInteractions',
  PractitionerIncome = 'practitionerIncome',
  Scheduling = 'scheduling',
  Transactions = 'transactions',
  TreatmentStepServiceCodes = 'treatmentStepServiceCodes',
  TreatmentStepTreatments = 'treatmentStepTreatments',
  SchedulingEvents = 'schedulingEvents',
}

export const PATIENT_COMPATIBLE_DATA_SOURCE_IDS: ReportBuilderDataSourceId[] = [
  ReportBuilderDataSourceId.AccountCredits,
  ReportBuilderDataSourceId.Appointments,
  ReportBuilderDataSourceId.Invoices,
  ReportBuilderDataSourceId.LabJobs,
  ReportBuilderDataSourceId.Patients,
  ReportBuilderDataSourceId.PatientInteractions,
  ReportBuilderDataSourceId.Transactions,
];
